import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { playClickSound } from "@/lib/sounds";
import { useAdminPendingCount } from "@/hooks/useAdminPendingCount";
import { useUserRoles } from "@/hooks/useUserRoles";
import { NavLink } from "@/components/NavLink";
import { Scale, PlayCircle, MapPin, Map, Search } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  GraduationCap,
  FileText,
  Brain,
  BarChart3,
  Target,
  Trophy,
  Clock,
  AlertTriangle,
  Settings,
  LogOut,
  FileQuestion,
  ArrowLeftRight,
  Timer,
  Plus,
  Minus,
  BookOpen,
  PenLine,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import brandLogoHorizontal from "@/assets/brand/nivela-logo-horizontal.png";
import brandIcon from "@/assets/brand/nivela-icon.png";

const LEVEL_NAMES = ['Iniciante', 'Estudante', 'Dedicado', 'Avançado', 'Expert', 'Mestre', 'Lenda'];

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

const studyItems: MenuItem[] = [
  { title: 'Questões ENEM', url: '/enem/questions', icon: FileQuestion },
  { title: 'Simulados', url: '/enem/simulations', icon: FileText },
  { title: 'Videoaulas', url: '/videoaulas', icon: PlayCircle },
  { title: 'Simulado Completo', url: '/enem/full-simulation', icon: Clock },
  { title: 'Redação', url: '/enem/redacao', icon: PenLine },
  { title: 'Pomodoro', url: '/enem/pomodoro', icon: Timer },
];

const planItems: MenuItem[] = [
  { title: 'Plano de Estudos', url: '/enem/study-plan', icon: Target },
  { title: 'Autoavaliação', url: '/enem/autoavaliacao', icon: Brain },
];

const reviewItems: MenuItem[] = [
  { title: 'Revisão Espaçada', url: '/enem/smart-review', icon: Brain },
  { title: 'Áreas Fracas', url: '/enem/weak-areas', icon: Target },
];

const analysisItems: MenuItem[] = [
  { title: 'Panorama ENEM', url: '/enem/estatisticas', icon: BarChart3 },
  { title: 'Onde eu me encaixo', url: '/enem/onde-eu-estou', icon: MapPin },
  { title: 'Mapa por estado', url: '/enem/mapa-ufs', icon: Map },
  { title: 'Buscar escola/município', url: '/enem/buscar-escola', icon: Search },
  { title: 'Padrões de Erro', url: '/enem/errors', icon: AlertTriangle },
  { title: 'Ranking', url: '/enem/ranking', icon: Trophy },
  { title: 'Conquistas', url: '/enem/achievements', icon: Trophy },
];

const settingsItems: MenuItem[] = [
  { title: 'Configurações', url: '/enem/settings', icon: Settings },
];

export function EnemSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { isAdmin, pendingCount } = useAdminPendingCount();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin: isAdminRole, isDeveloper } = useUserRoles();
  const visibleStudyItems = (isAdminRole || isDeveloper)
    ? studyItems
    : studyItems.filter((i) => i.url !== '/videoaulas');

  // Collapsible states
  const [studyOpen, setStudyOpen] = useState(true);
  const [planOpen, setPlanOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { profile, userInitials } = useProfile();

  const { data: userStats } = useQuery({
    queryKey: ['sidebar-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const [streakRes, pointsRes] = await Promise.all([
        supabase.from('user_streaks').select('current_streak').eq('user_id', user.id).maybeSingle(),
        supabase.from('user_points').select('current_level').eq('user_id', user.id).eq('module_type', 'enem').order('current_level', { ascending: false }).limit(1).maybeSingle(),
      ]);
      return {
        streak: streakRes.data?.current_streak || 0,
        level: pointsRes.data?.current_level || 1,
      };
    },
    enabled: !!user,
  });
  // Pending smart review count
  const { data: pendingReviewCount } = useQuery({
    queryKey: ['enem-sidebar-review-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const today = new Date().toISOString().split('T')[0];
      const { count } = await supabase
        .from('user_question_reviews')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .lte('next_review_date', today);
      return count || 0;
    },
    enabled: !!user,
    staleTime: 60000,
  });


  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const isActive = (url: string) => location.pathname === url;

  const renderMenuItems = (items: MenuItem[]) => (
    <SidebarMenu className="space-y-1 px-2">
      {items.map((item, index) => {
        const active = isActive(item.url);
        const badgeCount = item.url === '/enem/smart-review' ? (pendingReviewCount || 0) : 0;
        return (
          <SidebarMenuItem 
            key={item.url}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <SidebarMenuButton asChild tooltip={item.title}>
              <a 
                href={item.url} 
                onClick={(e) => { 
                  e.preventDefault(); 
                  playClickSound();
                  navigate(item.url); 
                }}
                className={cn(
                  "sidebar-menu-item flex items-center gap-3 px-3 py-2.5 rounded-full transition-all duration-200",
                  active 
                    ? "bg-gradient-to-r from-purple-500 to-violet-600 text-white font-medium" 
                    : "hover:bg-muted/60"
                )}
              >
                <item.icon className={cn(
                  "sidebar-icon h-4 w-4 flex-shrink-0",
                  active && "scale-110"
                )} />
                {!collapsed && <span className="truncate">{item.title}</span>}
                {!collapsed && badgeCount > 0 && (
                  <span className="ml-auto h-5 min-w-5 rounded-full bg-primary/20 text-primary text-xs font-medium flex items-center justify-center px-1.5">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  const CollapsibleHeader = ({ 
    label, 
    isOpen,
    onToggle 
  }: { 
    label: string; 
    isOpen: boolean;
    onToggle: () => void;
  }) => (
    <button 
      onClick={onToggle}
      className="sidebar-group-header flex items-center w-full px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground font-medium hover:text-foreground transition-colors"
    >
      <span>{label}</span>
      {isOpen ? (
        <Minus className="sidebar-expand-icon h-3.5 w-3.5 ml-auto" />
      ) : (
        <Plus className="sidebar-expand-icon h-3.5 w-3.5 ml-auto" />
      )}
    </button>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className="border-b border-border/50 p-4">
        <div className="flex items-center gap-3">
          {collapsed ? (
            <img src={brandIcon} alt="Nível A Educação" className="h-9 w-9 rounded-xl flex-shrink-0" />
          ) : (
            <img src={brandLogoHorizontal} alt="Nível A Educação" className="h-7 w-auto" />
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="flex flex-col px-2 py-4">
        {/* Dashboard */}
        <SidebarGroup className="px-2">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Dashboard ENEM">
                  <a 
                    href="/enem" 
                    onClick={(e) => { e.preventDefault(); navigate('/enem'); }}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-full transition-all duration-200",
                      isActive('/enem') 
                        ? "bg-gradient-to-r from-purple-500 to-violet-600 text-white font-medium" 
                        : "hover:bg-muted/60"
                    )}
                  >
                    <BarChart3 className={cn(
                      "h-4 w-4 flex-shrink-0",
                      isActive('/enem') && "scale-110"
                    )} />
                    {!collapsed && <span>Dashboard ENEM</span>}
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Estudar */}
        <Collapsible open={studyOpen} onOpenChange={setStudyOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Estudar" 
                  isOpen={studyOpen} 
                  onToggle={() => setStudyOpen(!studyOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(visibleStudyItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(visibleStudyItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {/* Planejamento */}
        <Collapsible open={planOpen} onOpenChange={setPlanOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Planejamento" 
                  isOpen={planOpen} 
                  onToggle={() => setPlanOpen(!planOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(planItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(planItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {/* Revisar */}
        <Collapsible open={reviewOpen} onOpenChange={setReviewOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Revisar" 
                  isOpen={reviewOpen} 
                  onToggle={() => setReviewOpen(!reviewOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(reviewItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(reviewItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {/* Analisar */}
        <Collapsible open={analysisOpen} onOpenChange={setAnalysisOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Analisar" 
                  isOpen={analysisOpen} 
                  onToggle={() => setAnalysisOpen(!analysisOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(analysisItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(analysisItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>

        {/* Configurações */}
        <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader 
                  label="Configurações" 
                  isOpen={settingsOpen} 
                  onToggle={() => setSettingsOpen(!settingsOpen)} 
                />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>
                {renderMenuItems(settingsItems)}
              </SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && (
              <SidebarGroupContent>
                {renderMenuItems(settingsItems)}
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </Collapsible>


      </SidebarContent>

      <SidebarFooter className="border-t border-border/50 p-4">
        {/* Trocar Módulo */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Trocar Módulo">
              <a
                href="/select-module"
                onClick={(e) => { e.preventDefault(); navigate("/select-module"); }}
                className={cn(
                  "flex items-center rounded-full border border-border bg-muted/50 hover:bg-muted transition-all duration-200 shadow-sm hover:shadow-md mb-2",
                  collapsed ? "justify-center w-10 h-10 p-0 mx-auto" : "gap-3 px-3 py-2.5"
                )}
              >
                <ArrowLeftRight className={cn("flex-shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
                {!collapsed && <span className="font-medium">Trocar Módulo</span>}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {isAdmin && (
          <NavLink to="/admin" className="block px-2 mb-2" activeClassName="">
            <div className="relative flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2 hover:bg-muted transition-colors">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              {!collapsed && <span className="text-xs text-muted-foreground">Painel Admin</span>}
              {pendingCount > 0 && (
                collapsed ? (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-destructive" />
                ) : (
                  <span className="ml-auto bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium min-w-[18px] text-center">
                    {pendingCount}
                  </span>
                )
              )}
            </div>
          </NavLink>
        )}
        {/* User Profile + Logout */}
        <SidebarMenu>
          <SidebarMenuItem>
            <div className={cn("flex items-center w-full", collapsed ? "flex-col gap-2" : "gap-2")}>
              <SidebarMenuButton tooltip={profile?.first_name || 'Perfil'} className="flex-1 min-w-0 overflow-visible"
                onClick={() => navigate('/profile')}
              >
                <div className="flex items-center gap-3 w-full group">
                  <Avatar className="h-9 w-9 flex-shrink-0 ring-2 ring-border transition-all duration-200 group-hover:ring-foreground/20">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-600 to-purple-600 text-white text-sm font-medium">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  {!collapsed && (
                    <div className="flex-1 text-left min-w-0">
                      <span className="text-sm font-medium truncate block max-w-[120px]">
                        {profile?.first_name || user?.email?.split('@')[0] || 'Usuário'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {userStats && `Nível ${userStats.level} • ${userStats.streak}🔥`}
                      </span>
                    </div>
                  )}
                </div>
              </SidebarMenuButton>
              <button 
                onClick={handleSignOut}
                className="p-2 rounded-xl hover:bg-muted/60 transition-all duration-200 text-destructive flex-shrink-0"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
